import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CircleAlert, CircleHelp, Grape, Info, Loader2, Radio, RefreshCw, TriangleAlert, Wifi } from "lucide-react";
import { PageHeader, Panel, Overline } from "@/components/Panel";
import { getFieldsHealth } from "@/api/treeplet";

const MILDEW_STATE = {
  NO_RISK: { label: "Sin riesgo", description: "No se ha detectado un episodio de riesgo." },
  RISK: { label: "Riesgo detectado", description: "Hay al menos un dispositivo con riesgo." },
  UNKNOWN: { label: "Datos insuficientes", description: "No hay datos suficientes para valorar el riesgo." },
};

const OPERATIONAL_STATE = {
  online: { label: "Operativo", description: "Los dispositivos están comunicando." },
  degraded: { label: "Atención operativa", description: "Algunos dispositivos no están comunicando." },
  offline: { label: "Sin conexión", description: "Ningún dispositivo está comunicando." },
  unknown: { label: "Estado desconocido", description: "Todavía no hay información operativa." },
};

const coverageLabel = (coverage) => ({
  COMPLETE: "Cobertura completa",
  PARTIAL: "Cobertura parcial",
  NONE: "Sin cobertura",
}[coverage] || "Cobertura desconocida");

const evaluationLabel = (status) => ({
  NEVER_EVALUATED: "Evaluación pendiente",
  STALE: "Evaluación obsoleta",
  CURRENT: "Evaluación actual",
}[status] || "Estado de evaluación desconocido");

const formatTime = (value) => {
  if (!value) return "Sin datos";
  return new Intl.DateTimeFormat("es-ES", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
};

export default function Overview() {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setError(null);
      const envelope = await getFieldsHealth();
      setFields(envelope.data || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar los datos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(load, 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const orderedFields = useMemo(() => [...fields].sort((a, b) => {
    if (Boolean(a.attention_required) !== Boolean(b.attention_required)) return a.attention_required ? -1 : 1;
    return a.field_name.localeCompare(b.field_name, "es");
  }), [fields]);
  const summary = useMemo(() => fields.reduce((result, field) => {
    const total = field.device_counts?.total || 0;
    const requiringAttention = field.operational?.devices_requiring_attention || 0;
    result.devices += total;
    if (field.operational?.effective_state === "online") result.online += total;
    if (field.operational?.effective_state === "degraded") result.online += Math.max(0, total - requiringAttention);
    result.riskFields += field.mildew_state === "RISK" ? 1 : 0;
    result.attentionFields += field.attention_required ? 1 : 0;
    return result;
  }, { devices: 0, online: 0, riskFields: 0, attentionFields: 0 }), [fields]);

  return (
    <div className="h-full flex flex-col animate-fade-in max-w-[1500px] mx-auto w-full">
      <PageHeader
        title="Resumen"
        subtitle="Estado operativo y salud de los campos monitorizados"
        testId="overview-header"
        actions={
          <button onClick={() => void load()} disabled={refreshing} className="flex items-center gap-2 text-sm font-semibold text-brand disabled:opacity-60" aria-busy={refreshing}>
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Actualizando…" : "Actualizar"}
          </button>
        }
      />

      {loading ? (
        <Panel className="flex-1 flex items-center justify-center" data-testid="overview-loading"><Loader2 className="animate-spin text-brand" size={32} /> <span className="sr-only">Cargando salud de los campos</span></Panel>
      ) : error ? (
        <Panel className="flex-1 flex flex-col items-center justify-center gap-3 text-critical" data-testid="overview-error">
          <CircleAlert size={30} />
          <span>{error}</span>
          <button onClick={() => void load()} className="text-sm font-semibold underline">Reintentar</button>
        </Panel>
      ) : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-4" data-testid="overview-health-summary">
            {[
              { label: "Campos", value: fields.length, icon: Grape },
              { label: "Dispositivos", value: summary.devices, icon: Radio },
              { label: "En línea", value: summary.online, icon: Wifi },
              { label: "Con riesgo", value: summary.riskFields, icon: TriangleAlert },
              { label: "Requieren atención", value: summary.attentionFields, icon: CircleAlert },
            ].map((item) => (
              <Panel key={item.label} className="p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><item.icon size={22} /></div>
                <div><div className="font-heading font-extrabold text-3xl">{item.value}</div><div className="text-xs text-ink-secondary">{item.label}</div></div>
              </Panel>
            ))}
          </div>

          <Panel className="p-5 flex-1 min-h-0 overflow-auto tp-scroll">
            <div className="flex items-center justify-between mb-4">
              <div><Overline>Cuenta actual</Overline><h2 className="font-heading font-bold text-lg mt-1">Salud de los campos</h2></div>
              <button onClick={() => navigate("/sensores")} className="text-sm font-semibold text-brand flex items-center gap-1">Ver dispositivos <ArrowRight size={14} /></button>
            </div>
            {fields.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-ink-tertiary" data-testid="overview-empty">Esta cuenta todavía no tiene campos.</div>
            ) : (
              <div className="space-y-3" data-testid="overview-field-health-list">
                {orderedFields.map((field) => {
                  const mildew = MILDEW_STATE[field.mildew_state] || MILDEW_STATE.UNKNOWN;
                  const operational = OPERATIONAL_STATE[field.operational?.effective_state] || OPERATIONAL_STATE.unknown;
                  const alert = field.active_alert;
                  const MildewIcon = field.mildew_state === "RISK" ? TriangleAlert : field.mildew_state === "NO_RISK" ? Info : CircleHelp;
                  const attentionDevices = field.operational?.devices_requiring_attention || 0;
                  const affectedDevices = alert?.affected_device_ids?.length || 0;
                  return (
                    <div key={field.field_id} className={`border rounded-lg p-4 ${field.attention_required ? "border-moderate-border bg-moderate-bg/20" : "border-line"}`} data-testid={`overview-field-${field.field_id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-heading font-bold text-ink">{field.field_name}</div>
                          <div className="text-xs text-ink-tertiary mt-1">{field.field_id} · {field.device_counts?.total || 0} dispositivo{field.device_counts?.total === 1 ? "" : "s"}</div>
                        </div>
                        {field.attention_required && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-moderate"><CircleAlert size={14} /> Requiere atención</span>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                        <div className="rounded-md border border-line bg-surface px-3 py-2.5" data-testid={`overview-field-mildew-${field.field_id}`}>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">Mildiu</div>
                          <div className="flex items-center gap-2 mt-1 font-semibold text-sm"><MildewIcon size={15} aria-hidden="true" /> {mildew.label}</div>
                          <div className="text-xs text-ink-secondary mt-1">{mildew.description}</div>
                          <div className="text-xs text-ink-tertiary mt-1">{coverageLabel(field.coverage)} · {evaluationLabel(field.evaluation_status)}</div>
                        </div>
                        <div className="rounded-md border border-line bg-surface px-3 py-2.5" data-testid={`overview-field-operational-${field.field_id}`}>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">Operación</div>
                          <div className="flex items-center gap-2 mt-1 font-semibold text-sm"><Wifi size={15} aria-hidden="true" /> {operational.label}</div>
                          <div className="text-xs text-ink-secondary mt-1">{operational.description}</div>
                          <div className="text-xs text-ink-tertiary mt-1">{attentionDevices} dispositivo{attentionDevices === 1 ? "" : "s"} con atención</div>
                        </div>
                      </div>
                      {field.mildew_state === "UNKNOWN" && (
                        <div className="mt-3 text-xs text-ink-secondary flex items-start gap-2"><CircleHelp size={14} className="mt-0.5 shrink-0" /> Los datos actuales no permiten confirmar un estado de riesgo.</div>
                      )}
                      {alert && (
                        <div className="mt-3 rounded-md border border-critical-border bg-critical-bg px-3 py-2.5 text-sm" data-testid={`overview-field-alert-${field.field_id}`}>
                          <div className="font-semibold text-critical">Alerta activa · {affectedDevices} dispositivo{affectedDevices === 1 ? "" : "s"}</div>
                          <div className="text-xs text-ink-secondary mt-1">Abierta: {formatTime(alert.opened_at)}. Acción recomendada: revisar la parcela y los dispositivos afectados.</div>
                        </div>
                      )}
                      <button onClick={() => navigate(`/sensores?field=${encodeURIComponent(field.field_id)}`)} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand" data-testid={`overview-field-devices-${field.field_id}`}>
                        Ver dispositivos de este campo <ArrowRight size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
